import { useState } from 'react';
import { ChevronRight, SearchIcon } from 'lucide-react';
import type { LabTestTVitalLabTestMetadataTVitalLabMarkerMetadataT as LabTestT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { Input } from '@/shared/ui/shadcn/input';

import { useLabTestSearch } from '../../model/useLabTestSearch';

interface LabTestSearchProps {
  onSelect: (labTest: LabTestT) => void;
  action?: React.ReactNode;
}

export function LabTestSearch({ onSelect, action }: LabTestSearchProps) {
  const [query, setQuery] = useState('');
  const labTestsQuery = useLabTestSearch({ name: query });
  const labTests = labTestsQuery.data?.labTests ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/60" />
          <Input
            placeholder="Search lab tests…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        {action}
      </div>

      {labTestsQuery.isLoading ? (
        <div className="space-y-3 py-2" role="status" aria-label="Loading lab tests">
          <div className="h-3 w-2/5 animate-pulse rounded-full bg-gradient-to-r from-muted/50 via-muted/80 to-muted/50" />
          <div className="h-3 w-3/5 animate-pulse rounded-full bg-gradient-to-r from-muted/40 via-muted/70 to-muted/40 [animation-delay:150ms]" />
          <div className="h-3 w-1/3 animate-pulse rounded-full bg-gradient-to-r from-muted/30 via-muted/60 to-muted/30 [animation-delay:300ms]" />
        </div>
      ) : labTestsQuery.isError ? (
        <p className="text-sm text-destructive">
          {labTestsQuery.error instanceof Error
            ? labTestsQuery.error.message
            : 'Failed to load lab tests.'}
        </p>
      ) : labTests.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {query.trim() === '' ? 'No lab tests available.' : 'No matching lab tests.'}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {labTests.map((t) => {
            const markers = t.markers ?? [];
            const sortedMarkers = [...markers].sort((a, b) => a.name.localeCompare(b.name));

            return (
              <li key={t.id}>
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-center gap-3 py-3 text-left transition-colors hover:bg-muted/40"
                  onClick={() => onSelect(t)}
                >
                  <div className="min-w-0 flex-1 space-y-2">
                    <div>
                      <p className="text-sm font-medium">{t.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {t.method.replace(/_/g, ' ')} • {t.sample_type.replace(/_/g, ' ')}
                      </p>
                    </div>
                    {sortedMarkers.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {sortedMarkers.map((m) => (
                          <span
                            key={m.id}
                            className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                          >
                            {m.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
